import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import {
  BarChart3,
  Flame,
  Target,
  TrendingUp,
  Trophy,
  Users,
} from 'lucide-react';
import { checkInService } from '@/services/checkin.service';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function CheckInAnalyticsPage() {
  const [dateRange, setDateRange] = useState('7');

  const getDateRange = () => {
    const days = parseInt(dateRange);
    const to = format(new Date(), 'yyyy-MM-dd');
    const from = format(subDays(new Date(), days), 'yyyy-MM-dd');
    return { from, to };
  };

  // Fetch analytics
  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['checkin-analytics', dateRange],
    queryFn: () => checkInService.getAnalytics(getDateRange()),
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch top streaks
  const { data: topStreaks, isLoading: streaksLoading } = useQuery({
    queryKey: ['top-streaks'],
    queryFn: () => checkInService.getTopStreaks(10),
  });

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Check-In Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Monitor daily check-in performance and streaks
          </p>
        </div>
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 Days</SelectItem>
            <SelectItem value="30">Last 30 Days</SelectItem>
            <SelectItem value="90">Last 90 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Today's Check-ins</p>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold">
              {analyticsLoading
                ? '...'
                : analytics?.todayCheckIns.toLocaleString() || 0}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Active Streaks</p>
              <Flame className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-3xl font-bold text-orange-500">
              {analyticsLoading
                ? '...'
                : analytics?.activeStreaks.toLocaleString() || 0}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">
                Milestone Achievements
              </p>
              <Target className="h-4 w-4 text-success" />
            </div>
            <p className="text-3xl font-bold text-success">
              {analyticsLoading
                ? '...'
                : analytics?.milestoneAchievements.toLocaleString() || 0}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Average Streak</p>
              <BarChart3 className="h-4 w-4 text-primary" />
            </div>
            <p className="text-3xl font-bold text-primary">
              {analyticsLoading
                ? '...'
                : analytics?.averageStreak.toFixed(1) || 0}
            </p>
            <p className="text-xs text-muted-foreground mt-1">days per user</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Check-In Chart */}
      {analytics?.dailyCheckInChart &&
        analytics.dailyCheckInChart.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Daily Check-Ins Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {analytics.dailyCheckInChart.map((item) => {
                  const maxCheckIns = Math.max(
                    ...analytics.dailyCheckInChart.map((d) => d.checkIns),
                  );
                  const widthPercent = Math.max(
                    5,
                    (item.checkIns / maxCheckIns) * 100,
                  );

                  return (
                    <div key={item.date} className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground w-24">
                        {format(new Date(item.date), 'MMM dd')}
                      </span>
                      <div className="flex-1 bg-secondary rounded-full h-6 overflow-hidden relative">
                        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                        {/* @ts-ignore - Dynamic width for progress bar */}
                        <div
                          className="bg-primary h-full flex items-center justify-end px-2 absolute left-0 top-0"
                          style={{ width: `${widthPercent}%` }}
                        >
                          <span className="text-xs font-medium text-primary-foreground">
                            {item.checkIns}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

      {/* Top Streaks Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Top Streaks Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          {streaksLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse h-16 bg-muted rounded" />
              ))}
            </div>
          ) : topStreaks && topStreaks.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Rank</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Current Streak</TableHead>
                  <TableHead className="text-right">Longest Streak</TableHead>
                  <TableHead className="text-right">Total Check-ins</TableHead>
                  <TableHead>Last Check-in</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topStreaks.map((streak, index) => (
                  <TableRow key={streak.userId}>
                    <TableCell>
                      {index < 3 ? (
                        <Badge
                          variant="secondary"
                          className={
                            index === 0
                              ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                              : index === 1
                                ? 'bg-gray-400 hover:bg-gray-500 text-white'
                                : 'bg-orange-600 hover:bg-orange-700 text-white'
                          }
                        >
                          {index + 1}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground ml-2">
                          {index + 1}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{streak.userName}</p>
                        <p className="text-sm text-muted-foreground">
                          {streak.userEmail}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="font-semibold">
                        <Flame className="h-3 w-3 mr-1 text-orange-500" />
                        {streak.currentStreak} days
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-muted-foreground">
                        {streak.longestStreak} days
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {streak.totalCheckIns.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {format(new Date(streak.lastCheckInDate), 'MMM dd, yyyy')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No streak data available</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default CheckInAnalyticsPage;
